/**
 * To Address → Ship To Additional Services tailgate preset (Pages).
 * IDs and field names come from page HTML / data-scm-* attributes, not task-specific code.
 */

import { getHardCodedIds } from "../../Utils/CommonFunctions";
import { getDataScmAttributes } from "../../Components/MarkupConversion/MarkupFunctions";

const readTruthyAttr = (attributes, keys, defaultValue = false) => {
  if (!attributes || typeof attributes !== "object") {
    return defaultValue;
  }
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (!Object.prototype.hasOwnProperty.call(attributes, key)) {
      continue;
    }
    const norm = String(attributes[key]).trim().toLowerCase();
    if (norm === "true" || norm === "1" || norm === "yes" || norm === "on") {
      return true;
    }
    if (norm === "false" || norm === "0" || norm === "no" || norm === "off") {
      return false;
    }
  }
  return defaultValue;
};

const parseCommaSeparatedFields = (raw) => {
  if (raw == null || raw === "") {
    return [];
  }
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Read tailgate preset settings from scm:inputtext attributes (and data-scm-*). */
export const parseTailgatePresetFromAttributes = (attributes = {}) => {
  const enabled = readTruthyAttr(
    attributes,
    ["tailgatePreset", "tailgatepreset"],
    false
  );
  if (!enabled) {
    return null;
  }

  const scm = getDataScmAttributes(
    attributes,
    () => null,
    null
  );

  const serviceId =
    scm.tailgateServiceId ||
    scm["tailgate-service-id"] ||
    attributes.tailgateServiceId ||
    attributes["data-scm-tailgate-service-id"] ||
    attributes["dataScmTailgateServiceId"] ||
    "";

  return {
    enabled: true,
    servicesField:
      attributes.tailgateServicesField ||
      attributes.tailgateservicesfield ||
      "to_additional_services",
    serviceCode: (
      attributes.tailgateServiceCode ||
      attributes.tailgateservicecode ||
      "TLGDEL"
    )
      .trim()
      .toUpperCase(),
    serviceName:
      attributes.tailgateServiceName ||
      attributes.tailgateservicename ||
      scm.tailgateServiceName ||
      "",
    serviceId: String(serviceId || "").trim(),
    statusFields: parseCommaSeparatedFields(
      attributes.tailgateStatusFields ||
        attributes.tailgatestatusfields ||
        "cf_taligate_status,cf_tailgate_status"
    ),
  };
};

const isTruthyCheckboxValue = (val) => {
  if (val === true || val === 1) {
    return true;
  }
  if (typeof val === "string") {
    const normalized = val.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

export const readTailgateStatusFromAddressRecord = (record, statusFields = []) => {
  if (!record || typeof record !== "object") {
    return false;
  }
  const fields =
    Array.isArray(statusFields) && statusFields.length > 0
      ? statusFields
      : ["cf_taligate_status", "cf_tailgate_status"];

  for (let i = 0; i < fields.length; i += 1) {
    const key = fields[i];
    if (isTruthyCheckboxValue(record[key])) {
      return true;
    }
    const lower = String(key).toLowerCase();
    const alt = Object.keys(record).find(
      (k) => String(k).toLowerCase() === lower
    );
    if (alt && isTruthyCheckboxValue(record[alt])) {
      return true;
    }
  }
  return false;
};

const normalizeLabel = (label) =>
  String(label || "")
    .trim()
    .toLowerCase();

const resolveTailgateServiceIdentity = (preset, existingEntries = []) => {
  if (preset.serviceId) {
    return {
      id: preset.serviceId,
      label:
        preset.serviceName ||
        getHardCodedIds("tailgateDeliveryAuthorizedLabel") ||
        "Tailgate Delivery Authorized",
    };
  }

  const code = preset.serviceCode;
  const nameHint = normalizeLabel(
    preset.serviceName ||
      getHardCodedIds("tailgateDeliveryAuthorizedLabel") ||
      "Tailgate Delivery Authorized"
  );

  for (let i = 0; i < existingEntries.length; i += 1) {
    const entry = existingEntries[i];
    const entryCode = String(entry.code || entry.service_code || "")
      .trim()
      .toUpperCase();
    if (code && entryCode === code) {
      return {
        id: String(entry.id ?? entry.value ?? ""),
        label: String(entry.label || entry.name || entry.text || nameHint),
      };
    }
    if (nameHint && normalizeLabel(entry.label || entry.name) === nameHint) {
      return {
        id: String(entry.id ?? entry.value ?? ""),
        label: String(entry.label || entry.name || entry.text),
      };
    }
  }

  const legacyId = getHardCodedIds("tailgateDeliveryAuthorizedId");
  if (legacyId) {
    return {
      id: String(legacyId),
      label:
        preset.serviceName ||
        getHardCodedIds("tailgateDeliveryAuthorizedLabel") ||
        "Tailgate Delivery Authorized",
    };
  }

  return null;
};

export const normalizeMultiLookupEntriesForTailgate = (raw) => {
  if (raw == null || raw === "") {
    return [];
  }
  let arr = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try {
        arr = JSON.parse(trimmed);
      } catch (e) {
        arr = [];
      }
    } else {
      arr = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(arr)) {
    arr = [arr];
  }
  return arr
    .map((item) => {
      if (item == null) {
        return null;
      }
      if (typeof item !== "object") {
        const value = String(item);
        return value ? { id: value, value, label: value, text: value } : null;
      }
      const value =
        item.value != null && item.value !== ""
          ? String(item.value)
          : String(item.id ?? "");
      if (!value) {
        return null;
      }
      const label = item.label || item.text || item.name || value;
      return {
        ...item,
        id: item.id != null && item.id !== "" ? String(item.id) : value,
        value,
        label: String(label),
        text: String(label),
      };
    })
    .filter(Boolean);
};

/**
 * When address tailgate status is true, add configured additional service; else remove it.
 * @returns next MultiLookup array, or null if unchanged
 */
export const applyTailgatePresetToServices = (
  currentValue,
  addressRecord,
  preset
) => {
  if (!preset?.enabled || !addressRecord || typeof addressRecord !== "object") {
    return null;
  }

  const entries = normalizeMultiLookupEntriesForTailgate(currentValue);
  const service = resolveTailgateServiceIdentity(preset, entries);
  if (!service?.id) {
    return null;
  }

  const serviceId = String(service.id).replace(/ggg$/, "");
  const serviceLabel = service.label;

  const isTailgateEntry = (entry) =>
    String(entry.value).replace(/ggg$/, "") === serviceId ||
    (preset.serviceCode &&
      String(entry.code || entry.service_code || "")
        .trim()
        .toUpperCase() === preset.serviceCode) ||
    (serviceLabel &&
      normalizeLabel(entry.label || entry.name) === normalizeLabel(serviceLabel));

  if (readTailgateStatusFromAddressRecord(addressRecord, preset.statusFields)) {
    if (entries.some(isTailgateEntry)) {
      return null;
    }
    return [
      ...entries,
      {
        id: serviceId,
        value: serviceId,
        label: serviceLabel,
        text: serviceLabel,
        code: preset.serviceCode,
      },
    ];
  }

  if (!entries.some(isTailgateEntry)) {
    return null;
  }
  return entries.filter((entry) => !isTailgateEntry(entry));
};
